import {
  AdminCompleteFailedPartnerDriverStep,
  AdminCompletePartnerDriverBackgroundCheck,
  AdminCompletePartnerDriverDotNumber,
  AdminCompletePartnerDriverInsuranceDeclaration,
  AdminCompletePartnerDriverLicense,
  AdminCompletePartnerDriverLmsTraining,
  AdminCompletePartnerDriverVehicleInsurance,
  AdminCompletePartnerDriverVehicleRegistration,
  AdminDeletePartnerDriverData,
  AdminDeletePartnerDriverStepDocuments,
  AdminFetchPartnerDriver,
  AdminFetchPartnerDriverSummary,
  AdminResetPartnerDriverStep,
  AdminSearchPartnerDrivers,
  AdminSkipPartnerDriverStep,
  AdminSwitchPartnerDriverOnboarding,
  AdminUpdatePartnerDriver,
  AdminUpdatePartnerDriverTemplate,
  AdminUploadPartnerDriverStepDocuments,
  ClearPartnerDriverState,
} from 'src/constants/actionTypes'
import type {
  FetchPartnerDriverSuccessPayload,
  PartnerDriverIdPayload,
  PartnerDriverSearchRow,
} from 'src/interfaces/partnerDriver'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { PartnerDriverState } from './types'

const getInitialState = (): PartnerDriverState => {
  return {
    searchResults: [],
    searchLoading: false,
    searchError: null,
    driver: null,
    driverId: null,
    driverLoading: false,
    driverError: null,
    driverNotFound: false,
    summary: null,
    summaryLoading: false,
    summaryError: null,
    saving: false,
  }
}

// Writes never touch `driver` — the page refetches on success, which stays the single source of
// truth (the markets slice does the same). They only drive the shared `saving` flag.
const WRITE_ACTIONS = [
  AdminUpdatePartnerDriver,
  AdminUpdatePartnerDriverTemplate,
  AdminSwitchPartnerDriverOnboarding,
  AdminResetPartnerDriverStep,
  AdminCompleteFailedPartnerDriverStep,
  AdminUploadPartnerDriverStepDocuments,
  AdminDeletePartnerDriverStepDocuments,
  AdminCompletePartnerDriverBackgroundCheck,
  AdminCompletePartnerDriverVehicleInsurance,
  AdminCompletePartnerDriverLicense,
  AdminCompletePartnerDriverLmsTraining,
  AdminCompletePartnerDriverInsuranceDeclaration,
  AdminCompletePartnerDriverVehicleRegistration,
  AdminCompletePartnerDriverDotNumber,
  AdminSkipPartnerDriverStep,
  AdminDeletePartnerDriverData,
]

const WRITE_REQUEST_TYPES: string[] = WRITE_ACTIONS.map((writeAction) => {
  return writeAction.type.REQUEST
})

const WRITE_SETTLED_TYPES: string[] = WRITE_ACTIONS.flatMap((writeAction) => {
  return [writeAction.type.SUCCESS, writeAction.type.FAILED]
})

export const partnerDriverReducer = (
  state: PartnerDriverState = getInitialState(),
  action: Action<unknown>,
): PartnerDriverState => {
  // The one write that patches state: a deleted driver must not stay in the search results the
  // page returns to. Checked before the generic settle below, which would return first.
  if (action.type === AdminDeletePartnerDriverData.type.SUCCESS) {
    const deletedId = (action.payload as PartnerDriverIdPayload | undefined)?.partnerDriverId
    const searchResults = state.searchResults.filter((row) => {
      return row.id !== deletedId
    })

    return { ...state, searchResults, saving: false }
  }

  if (WRITE_REQUEST_TYPES.includes(action.type)) {
    return { ...state, saving: true }
  }

  if (WRITE_SETTLED_TYPES.includes(action.type)) {
    return { ...state, saving: false }
  }

  switch (action.type) {
    case AdminSearchPartnerDrivers.type.REQUEST:
      return { ...state, searchLoading: true, searchError: null }
    case AdminSearchPartnerDrivers.type.SUCCESS: {
      const searchResults = (action.payload as PartnerDriverSearchRow[] | undefined) ?? []

      return { ...state, searchResults, searchLoading: false, searchError: null }
    }
    case AdminSearchPartnerDrivers.type.FAILED:
      return {
        ...state,
        searchResults: [],
        searchLoading: false,
        searchError: resolveErrorMessage(action.payload, 'Failed to search drivers'),
      }

    case AdminFetchPartnerDriver.type.REQUEST: {
      const partnerDriverId =
        (action.payload as PartnerDriverIdPayload | undefined)?.partnerDriverId ?? null
      const isSameDriver = partnerDriverId !== null && partnerDriverId === state.driverId

      return {
        ...state,
        driver: isSameDriver ? state.driver : null,
        driverId: partnerDriverId,
        driverLoading: true,
        driverError: null,
        driverNotFound: false,
      }
    }
    case AdminFetchPartnerDriver.type.SUCCESS: {
      const payload = action.payload as FetchPartnerDriverSuccessPayload

      if (payload.partnerDriverId !== state.driverId) {
        return state
      }

      return {
        ...state,
        driver: payload.driver,
        driverLoading: false,
        driverError: null,
        driverNotFound: payload.driver === null,
      }
    }
    case AdminFetchPartnerDriver.type.FAILED:
      // A read that fails after the page cleared the state must not write into the next page.
      if (state.driverId === null) {
        return state
      }

      return {
        ...state,
        driverLoading: false,
        driverError: resolveErrorMessage(action.payload, 'Failed to load the driver'),
      }

    case AdminFetchPartnerDriverSummary.type.REQUEST: {
      const partnerDriverId = (action.payload as PartnerDriverIdPayload | undefined)
        ?.partnerDriverId
      const isSameDriver = state.summary !== null && state.summary.id === partnerDriverId

      return {
        ...state,
        summary: isSameDriver ? state.summary : null,
        summaryLoading: true,
        summaryError: null,
      }
    }
    case AdminFetchPartnerDriverSummary.type.SUCCESS:
      return {
        ...state,
        summary: (action.payload as PartnerDriverSearchRow | null | undefined) ?? null,
        summaryLoading: false,
        summaryError: null,
      }
    case AdminFetchPartnerDriverSummary.type.FAILED:
      if (state.driverId === null) {
        return state
      }

      return {
        ...state,
        summaryLoading: false,
        summaryError: resolveErrorMessage(action.payload, 'Failed to load background checks'),
      }

    case ClearPartnerDriverState.type:
      return { ...getInitialState(), searchResults: state.searchResults }

    default:
      return state
  }
}
